import { Phone, ArrowUpRight, Zap } from "lucide-react";
import { BUSINESS } from "@/lib/business";

export const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen pt-20 overflow-hidden bg-[#0A0A0A]"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.prod-images.emergentagent.com/jobs/d0317d37-a927-456c-8531-810376042e6c/images/38ec0227d8e033e56f61edb8630468e917ce5bfc5ddd16cf4fddb3e38d2d27fe.png"
          alt="Electrician at work"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-[#0A0A0A]/90 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/60" />
      </div>

      {/* Faint blue glow */}
      <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-[#1E90FF]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
        <div className="max-w-3xl">
          {/* Overline */}
          <div className="flex items-center gap-3 mb-8 fade-up">
            <span className="w-2 h-2 bg-[#1E90FF] rounded-full pulse-dot" />
            <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#1E90FF]">
              Medway, Kent — Available now
            </span>
          </div>

          {/* H1 */}
          <h1
            data-testid="hero-headline"
            className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-semibold text-white leading-[0.95] tracking-tight fade-up"
            style={{ fontFamily: "Outfit", animationDelay: "0.1s" }}
          >
            Premium electrical work.
            <br />
            <span className="text-[#A0AAB2] italic font-light" style={{ fontFamily: "Outfit" }}>
              Done properly.
            </span>
          </h1>

          {/* Sub */}
          <p
            className="mt-8 text-lg sm:text-xl text-[#A0AAB2] leading-relaxed max-w-2xl fade-up"
            style={{ animationDelay: "0.2s" }}
          >
            Qualified, fully insured electricians serving Medway and surrounding areas.
            From full domestic rewires to a single light fitting — Ernest and the Optima
            team deliver every job to the highest standard.
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
            <a
              href="#contact"
              data-testid="hero-quote-button"
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#1E90FF] hover:bg-[#0073E6] text-white font-medium tracking-wide transition-all duration-300"
            >
              <span>Request a free quote</span>
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
            </a>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              data-testid="hero-call-button"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-white/15 hover:border-[#1E90FF] hover:bg-white/5 text-white font-medium tracking-wide transition-all duration-300"
            >
              <Phone className="w-4 h-4" />
              <span>{BUSINESS.phone}</span>
            </a>
            <a
              href="https://wa.me/447511625975"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group inline-flex items-center gap-3 px-7 py-4 border border-white/15 hover:border-[#25D366] hover:bg-[#25D366]/10 text-white font-medium tracking-wide transition-all duration-300"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-[#25D366]"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884"/></svg>
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mini stats */}
          <div
            className="mt-16 lg:mt-20 grid grid-cols-2 sm:grid-cols-4 gap-8 border-t border-white/5 pt-10 fade-up"
            style={{ animationDelay: "0.4s" }}
          >
            {[
              { k: "10+", v: "Years experience" },
              { k: "500+", v: "Jobs completed" },
              { k: "5★", v: "Facebook rated" },
              { k: "24/7", v: "Emergency callouts" },
            ].map((s) => (
              <div key={s.v}>
                <div
                  className="text-3xl sm:text-4xl text-white font-semibold"
                  style={{ fontFamily: "Outfit" }}
                >
                  {s.k}
                </div>
                <div className="mt-1 text-xs text-[#A0AAB2] uppercase tracking-[0.2em]">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom corner mark */}
      <div className="absolute bottom-8 right-8 hidden lg:flex items-center gap-2 text-[#A0AAB2] text-xs tracking-[0.3em] uppercase">
        <Zap className="w-3 h-3 text-[#1E90FF]" strokeWidth={2.5} />
        <span>Est. Medway</span>
      </div>
    </section>
  );
};
